import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tag } from '@duxton-web/ui';
import debounce from 'lodash/debounce';

import { AccessScopeOption, buildAccessScopeOptions } from './utils';
import CommonSelect from '@/common/selects/CommonSelect';
import {
  useGetServices,
  useGetTechFamilies as useGetTechFamiliesFromService,
} from '@/hooks/usePlannerQueries/service-onboarding';

const SEARCH_MIN_LENGTH = 2;
const DEBOUNCE_DELAY_MS = 300;

type AccessScopeSelectProps = {
  value: string;
  onChange: (value: string) => void;
};

const renderAccessScopeLabel = (option: AccessScopeOption) => (
  <div className="access-control-scope-option">
    <span>{option.label}</span>
    <Tag size="xsmall" color={option.type === 'service' ? 'success' : 'blue'}>
      {option.type === 'service' ? 'Service' : 'Tech family'}
    </Tag>
  </div>
);

export const AccessScopeSelect = ({
  value,
  onChange,
}: AccessScopeSelectProps) => {
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const debouncedSetSearch = useMemo(
    () =>
      debounce(
        (search: string) => setDebouncedSearch(search),
        DEBOUNCE_DELAY_MS,
      ),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearch = useCallback(
    (search: string) => {
      if (search.length === 0 || search.length >= SEARCH_MIN_LENGTH) {
        debouncedSetSearch(search);
        return;
      }

      debouncedSetSearch.cancel();
      setDebouncedSearch('');
    },
    [debouncedSetSearch],
  );

  const activeSearch =
    debouncedSearch.length >= SEARCH_MIN_LENGTH ? debouncedSearch : undefined;
  const hasActiveSearch = activeSearch !== undefined;

  const { data: servicesData } = useGetServices(activeSearch, {
    enabled: hasActiveSearch,
  });
  const { data: techFamiliesData } = useGetTechFamiliesFromService(
    activeSearch,
    {
      enabled: hasActiveSearch,
    },
  );
  const options = useMemo(
    () => buildAccessScopeOptions(servicesData, techFamiliesData),
    [servicesData, techFamiliesData],
  );
  const handleChange = useCallback(
    (selectedValue: string | undefined) =>
      onChange(typeof selectedValue === 'string' ? selectedValue : ''),
    [onChange],
  );

  return (
    <CommonSelect
      allowClear
      className="access-control-filter-select"
      data-testid="access-control-scope-select"
      idKey="id"
      placeholder="Search service or tech family"
      value={value}
      values={options}
      valueFn={renderAccessScopeLabel}
      onChange={handleChange}
      onSearch={handleSearch}
    />
  );
};

export default AccessScopeSelect;
